import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import { useEffect, useState } from 'react';
// Helpers
import { yupEmailRequiredValidation } from 'helpers/validation';
// Hooks
import { useCountdown } from 'hooks';
// Components
import { COUNTDOWN_TIME } from '../OTPForm/OTPForm';
// Ui3
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import Input from 'ui3/Input/Input';
// Styles
import styles from './EmailForm.module.scss';

type MailFormProps = {
  email: string;
  manualInput?: boolean;
  initialTimeLeft?: number;
  onSendCode: (email?: string) => void;
  setCustomEmail: (email: string) => void;
};

type EmailFormData = {
  email: string;
};

const getValidationSchema = () => {
  return yup.object().shape({
    email: yupEmailRequiredValidation('Email is required'),
  });
};

const MailForm = ({
  email,
  onSendCode,
  manualInput = false,
  setCustomEmail,
  initialTimeLeft = 0,
}: MailFormProps) => {
  const [codeAlreadySent, setCodeAlreadySent] = useState(false);
  const { isActive, formatTime, start } = useCountdown();
  const methods = useForm<EmailFormData>({
    resolver: yupResolver(getValidationSchema()),
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!codeAlreadySent && initialTimeLeft > 0) {
      start(initialTimeLeft);
      setCodeAlreadySent(true);
    }
  }, [codeAlreadySent, initialTimeLeft, start]);

  const handleSendCode = () => {
    onSendCode();
    start(initialTimeLeft || COUNTDOWN_TIME);
    setCodeAlreadySent(true);
  };

  const handleFormSubmit = methods.handleSubmit(
    async (values: EmailFormData) => {
      try {
        onSendCode(values.email);
        setCustomEmail(values.email);
        start(initialTimeLeft || COUNTDOWN_TIME);
        setCodeAlreadySent(true);
      } catch (error) {
        console.log(error);
      }
    }
  );

  const renderEmailInputForm = () => {
    const emailWatcher = methods.watch('email');
    return (
      <>
        <Text variant="body1Regular16" className={styles.description}>
          Please enter your email address so that we can send you the
          verification code
        </Text>
        <Input
          className={styles.input}
          type="email"
          icon="mail"
          placeholder="E-mail address"
          label="E-mail"
          name="email"
          ref={methods.register}
          error={methods.errors?.email?.message}
        />
        <div className={styles.footer}>
          <Button
            variant="primary"
            size="medium"
            onClick={handleFormSubmit}
            disabled={
              !!methods.errors?.email || emailWatcher.length === 0 || isActive
            }
          >
            {isActive ? `Send Code (${formatTime()})` : 'Send Code'}
          </Button>
        </div>
      </>
    );
  };

  const renderPreFilledSendMailForm = () => {
    return (
      <>
        <Text variant="body1Regular16" className={styles.description}>
          We&apos;ll send an email with a verification code to your {email}
        </Text>
        <div className={styles.footer}>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSendCode}
            disabled={isActive}
          >
            {isActive ? `Send Code (${formatTime()})` : 'Send Code'}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className={styles.root}>
      {manualInput ? renderEmailInputForm() : renderPreFilledSendMailForm()}
    </div>
  );
};

export default MailForm;
